import { useAppSelector } from '@/app/redux/hooks';
import { useStudents } from '@/app/redux/hooks/students';
import { selectUserId } from '@/app/redux/slices/sessionSlice';
import {
  DashboardViewActions,
  DashboardViewContainer,
  DashboardViewContent,
  DashboardViewHeader,
  DashboardViewTitle,
} from '@/common/ui/DashboardViewStyles';
import { ActionLoader } from '@/common/ui/LoadingSpinners';
import SearchInput from '@/common/ui/SearchInput';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import { PlusIcon, UsersFourIcon } from '@phosphor-icons/react';
import { Badge } from '@shadcn/components/badge';
import { Button } from '@shadcn/components/button';
import { useEffect, useMemo, useState } from 'react';
import 'react-loading-skeleton/dist/skeleton.css';
import { useNavigate } from 'react-router-dom';
import LearnerList from './LearnerList/LearnerList';
import StudentCreationModal from './StudentCreationModal';

export default function LearnersView() {
  const navigate = useNavigate();

  const { students, isLoading, isFetching } = useStudents();

  const userId = useAppSelector(selectUserId);

  const [addStudentModalOpen, setAddStudentModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 150);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const filteredStudents = useMemo(() => {
    if (!debouncedSearchTerm) return students;
    const terms = debouncedSearchTerm.toLowerCase().split(' ');
    return students.filter((student) => {
      const key =
        `${student.studentFirstName} ${student.studentSurname} ${student.spellcastUsername}`.toLowerCase();
      return terms.every((term) => key.includes(term));
    });
  }, [debouncedSearchTerm, students]);

  useEffect(() => {
    if (!debouncedSearchTerm) return;
    trackMixpanelEvent('Search Query Entered', {
      view: 'learners',
      searchTerm: debouncedSearchTerm,
      resultsCount: filteredStudents.length,
    });
  }, [debouncedSearchTerm, userId, filteredStudents.length]);

  const handleBulkAdd = () => {
    navigate('/dashboard/bulkadd');
  };

  function handleOpenAddStudentModal() {
    setAddStudentModalOpen(true);
  }

  return (
    <DashboardViewContainer>
      <DashboardViewHeader>
        <DashboardViewTitle>
          Learners
          <Badge className="px-1 text-xxs" variant="secondary">
            {isLoading || isFetching ? (
              <ActionLoader className="size-3" />
            ) : (
              students.length
            )}
          </Badge>
        </DashboardViewTitle>
        <DashboardViewActions>
          <SearchInput
            placeholder="Search learners..."
            value={searchTerm}
            onChange={setSearchTerm}
          />
          <Button
            variant="outline"
            className="flex gap-2"
            onClick={handleOpenAddStudentModal}
          >
            <PlusIcon size={20} />
            Add learner
          </Button>
          <Button
            variant="outline"
            className="flex gap-2"
            onClick={handleBulkAdd}
          >
            <UsersFourIcon size={20} />
            Bulk add learners
          </Button>
        </DashboardViewActions>
      </DashboardViewHeader>
      <DashboardViewContent>
        <LearnerList
          students={filteredStudents}
          onAddLearner={handleOpenAddStudentModal}
          onBulkAdd={handleBulkAdd}
          hideAddLearnerCta={!!searchTerm}
        />
      </DashboardViewContent>

      <StudentCreationModal
        open={addStudentModalOpen}
        onClose={() => setAddStudentModalOpen(false)}
      />
    </DashboardViewContainer>
  );
}
